import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  MessageSquareText,
  Send,
  Loader2,
  AtSign,
  User,
  Trash2,
  Clock,
  AlertCircle,
  Check,
  BellOff,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

export const InternalNotes = ({ customerId, customerName }) => {
  const [searchParams] = useSearchParams();
  const highlightNoteId = searchParams.get('highlight');
  
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const highlightedNoteRef = useRef(null);

  useEffect(() => {
    fetchNotes();
    fetchUsers();
  }, [customerId]);

  useEffect(() => {
    // Scroll to highlighted note when notes load
    if (highlightNoteId && highlightedNoteRef.current) {
      setTimeout(() => {
        highlightedNoteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [notes, highlightNoteId]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers/${customerId}/internal-notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSubmit = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/customers/${customerId}/internal-notes`, {
        content: newNote,
        tagged_user_ids: taggedUsers.map(u => u.id)
      });
      
      setNotes([response.data, ...notes]);
      setNewNote('');
      setTaggedUsers([]);
      
      if (taggedUsers.length > 0) {
        toast.success(`Note added and ${taggedUsers.length} user(s) notified`);
      } else {
        toast.success('Note added');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await api.delete(`/customers/${customerId}/internal-notes/${noteId}`);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    setNewNote(value);
    setCursorPosition(position);

    // Check if user typed @
    const lastAtIndex = value.lastIndexOf('@', position);
    if (lastAtIndex !== -1 && lastAtIndex === position - 1) {
      setShowUserPicker(true);
      setSearchTerm('');
    } else if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1, position);
      if (!textAfterAt.includes(' ')) {
        setShowUserPicker(true);
        setSearchTerm(textAfterAt);
      } else {
        setShowUserPicker(false);
      }
    } else {
      setShowUserPicker(false);
    }
  };

  const handleTagUser = (user) => {
    // Find the @ symbol position
    const lastAtIndex = newNote.lastIndexOf('@', cursorPosition);
    if (lastAtIndex !== -1) {
      const before = newNote.substring(0, lastAtIndex);
      const after = newNote.substring(cursorPosition);
      const newText = `${before}@${user.name}${after}`;
      setNewNote(newText);
      
      // Add to tagged users if not already tagged
      if (!taggedUsers.find(u => u.id === user.id)) {
        setTaggedUsers([...taggedUsers, user]);
      }
    }
    setShowUserPicker(false);
    textareaRef.current?.focus();
  };

  const removeTaggedUser = (userId) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !taggedUsers.find(t => t.id === user.id)
  );

  // Function to render note content with highlighted @mentions
  const renderNoteContent = (content, taggedUsers = []) => {
    if (!taggedUsers || taggedUsers.length === 0) {
      return <span>{content}</span>;
    }

    let result = content;
    taggedUsers.forEach(user => {
      const mention = `@${user.name}`;
      result = result.split(mention).join(`|||MENTION:${user.name}|||`);
    });

    const parts = result.split('|||');
    return parts.map((part, index) => {
      if (part.startsWith('MENTION:')) {
        const name = part.replace('MENTION:', '');
        return (
          <span 
            key={index} 
            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium"
          >
            <AtSign className="w-3 h-3" />
            {name}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MessageSquareText className="w-5 h-5 text-amber-600" />
              Internal Notes
            </CardTitle>
            <CardDescription>
              Private notes visible only to staff. Not visible to the customer or portal. Use @mention to notify team members.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            <AlertCircle className="w-3 h-3 mr-1" />
            Staff Only
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Note Form */}
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={newNote}
              onChange={handleTextChange}
              placeholder="Add a note... Use @name to mention team members"
              rows={3}
              className="resize-none pr-10"
              data-testid="internal-note-input"
            />
            <div className="absolute right-2 top-2">
              <Popover open={showUserPicker} onOpenChange={setShowUserPicker}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      setShowUserPicker(true);
                      setSearchTerm('');
                    }}
                    title="Mention someone"
                  >
                    <AtSign className="w-4 h-4 text-slate-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="end">
                  <div className="p-2 border-b">
                    <p className="text-sm font-medium text-slate-700">Tag a team member</p>
                  </div>
                  <ScrollArea className="h-48">
                    {filteredUsers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No users found
                      </div>
                    ) : (
                      <div className="p-1">
                        {filteredUsers.map((user) => (
                          <button
                            key={user.id}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left transition-colors"
                            onClick={() => handleTagUser(user)}
                            data-testid={`tag-user-${user.id}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{user.name}</p>
                              <p className="text-xs text-slate-500">{user.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Tagged Users */}
          {taggedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-slate-500">Will notify:</span>
              {taggedUsers.map((user) => (
                <Badge 
                  key={user.id} 
                  className="bg-blue-100 text-blue-700 gap-1 pr-1"
                >
                  <AtSign className="w-3 h-3" />
                  {user.name}
                  <button 
                    onClick={() => removeTaggedUser(user.id)}
                    className="ml-1 hover:bg-blue-200 rounded p-0.5"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !newNote.trim()}
              data-testid="submit-note-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Add Note
            </Button>
          </div>
        </div>

        {/* Notes List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <MessageSquareText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No internal notes yet</p>
            <p className="text-sm">Add a note above to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => {
              const isSystem = note.type === 'system';
              const isChangeLog = note.type === 'change_log';
              const isAutoNote = isSystem || isChangeLog;
              
              return (
              <div 
                key={note.id}
                ref={note.id === highlightNoteId ? highlightedNoteRef : null}
                className={`p-4 rounded-lg border transition-all duration-500 ${
                  note.id === highlightNoteId 
                    ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200' 
                    : isSystem 
                      ? 'bg-slate-50 border-slate-200'
                      : isChangeLog 
                        ? 'bg-blue-50/50 border-blue-100'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`note-${note.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSystem ? 'bg-amber-100' : isChangeLog ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      {isSystem ? (
                        <BellOff className="w-4 h-4 text-amber-600" />
                      ) : isChangeLog ? (
                        <FileText className="w-4 h-4 text-blue-600" />
                      ) : (
                        <User className="w-4 h-4 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${isAutoNote ? 'text-slate-600 text-sm' : 'text-slate-900'}`}>
                          {note.author_name}
                        </span>
                        {isSystem && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">System</Badge>
                        )}
                        {isChangeLog && (
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">Change Log</Badge>
                        )}
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(note.created_at)}
                        </span>
                        {note.id === highlightNoteId && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            New mention
                          </Badge>
                        )}
                      </div>
                      <div className={`mt-2 whitespace-pre-wrap ${isAutoNote ? 'text-slate-600 text-sm' : 'text-slate-700'}`}>
                        {renderNoteContent(note.content, note.tagged_users)}
                      </div>
                      {note.tagged_users && note.tagged_users.length > 0 && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-500">Mentioned:</span>
                          {note.tagged_users.map((user) => (
                            <Badge 
                              key={user.id} 
                              variant="outline" 
                              className="text-xs"
                            >
                              <Check className="w-3 h-3 mr-1 text-green-600" />
                              {user.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isAutoNote && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-red-600"
                    onClick={() => handleDeleteNote(note.id)}
                    data-testid={`delete-note-${note.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
